import { useEffect, useRef } from 'react'
import { QuestaoModel, TIPO_QUESTAO } from '../../models/QuestaoModel'
import { questaoEditorStore } from '../../stores/questaoEditorStore'
import { MultiplaEscolha } from './variacoes/MultiplaEscolha'
import { RelacionarItens } from './variacoes/RelacionarItens'
import { RelacionarItemImagem } from './variacoes/RelacionarItemImagem'
import { Button } from '../semantic-ui-antd-adapter'
import { CustomEditor } from '../CustomEditor/CustomEditor'
import { MultiploVerdadeiroFalso } from './variacoes/MultiploVerdadeiroFalso'
import styled from '@emotion/styled'
import { Modal } from 'antd'

export enum TIPO_BOTAO {
  REVISAR = 'REVISAR',
  SALVAR = 'SALVAR',
  CANCELAR = 'CANCELAR',
  EXCLUIR = 'EXCLUIR',
}

interface IProps {
  questaoId?: string
  buttons?: TIPO_BOTAO[]
  local: 'AVALIACAO' | 'CONTEUDO'
  moduloId: number
  clearOnCancel?: boolean
  tipoQuestao?: TIPO_QUESTAO
  onQuestaoSave?: (questao: QuestaoModel) => void
  onQuestaoReview?: (questao: QuestaoModel) => void
  onQuestaoDelete?: (questaoId: number | string) => void
  onCancel?: () => void
}

const createTemporaryQuestaoId = () => `${Math.random() * -1}`

const normalizePersistedQuestaoId = (questaoId?: string): string | null => {
  if (!questaoId) return null
  const numericId = Number(questaoId)
  if (!Number.isFinite(numericId)) return null
  if (!Number.isInteger(numericId)) return null
  if (numericId <= 0) return null
  return `${numericId}`
}

export const QuestaoEditor = (props: IProps) => {
  const { questaoId: propQuestaoId, moduloId, local, tipoQuestao } = props
  const normalizedPersistedQuestaoId = normalizePersistedQuestaoId(propQuestaoId)
  const refQuestaoId: any = useRef(normalizedPersistedQuestaoId ?? createTemporaryQuestaoId())
  const questaoId = refQuestaoId.current
  const dispatch = questaoEditorStore.useDispatch()
  const allState = questaoEditorStore.useState()
  const state = allState[questaoId] || { questao: {} }
  const questaoTipoAtual = state?.questao?.tipoQuestao

  useEffect(() => {
    dispatch(questaoEditorStore.actions.pushQuestaoItem({ questaoId }))
    if (!refQuestaoId.current) refQuestaoId.current = createTemporaryQuestaoId()
    dispatch(questaoEditorStore.actions.setTipoQuestao({ tipoQuestao, questaoId }))
    return () => {
      refQuestaoId.current = null
    }
  }, [])
  useEffect(() => {
    if (!normalizedPersistedQuestaoId) return
    dispatch(questaoEditorStore.thunks.fetchQuestao(normalizedPersistedQuestaoId))
    refQuestaoId.current = questaoId
  }, [questaoId, dispatch, normalizedPersistedQuestaoId])
  useEffect(() => {
    dispatch(questaoEditorStore.actions.setLocal({ local, questaoId }))
  }, [local, dispatch, questaoId])
  useEffect(() => {
    dispatch(questaoEditorStore.actions.setModuloId({ moduloId, questaoId }))
  }, [moduloId, dispatch, questaoId])

  function selecionaTipoQuestao(_tipoQuestao: TIPO_QUESTAO) {
    dispatch(questaoEditorStore.actions.setTipoQuestao({ tipoQuestao: _tipoQuestao, questaoId }))
  }

  function updateEnunciado(e) {
    dispatch(
      questaoEditorStore.actions.setEnunciado({
        questaoId: questaoId,
        textoQuestao: e.currentTarget.innerText,
      })
    )
  }

  if (!state.questao) return null
  return (
    <EditorLayout className={state.hasChange ? 'questao-has-change' : 'questao-no-change'}>
      <Show condition={questaoTipoAtual}>
        <SectionHeader>Enunciado</SectionHeader>
        <EditorBlock>
          <CustomEditor
            text={state.questao.textoQuestao}
            onBlur={updateEnunciado}
            style={{ minHeight: '6em' }}
            toolbarType="FIXED"
            placeholder="Digite o enunciado da questão"
          />
        </EditorBlock>
        <SectionContent>
          {questaoTipoAtual === TIPO_QUESTAO.MULTIPLA_ESCOLHA && (
            <MultiplaEscolha questaoId={questaoId} />
          )}
          {questaoTipoAtual === TIPO_QUESTAO.RELACIONAR_ITENS && (
            <RelacionarItens questaoId={questaoId} />
          )}
          {questaoTipoAtual === TIPO_QUESTAO.RELACIONAR_ITEM_A_IMAGEM && (
            <RelacionarItemImagem questaoId={questaoId} />
          )}
          {questaoTipoAtual === TIPO_QUESTAO.MULTIPLO_VERDADEIRO_FALSO && (
            <MultiploVerdadeiroFalso questaoId={questaoId} />
          )}
        </SectionContent>
      </Show>
      <Show condition={!questaoTipoAtual}>
        <NenhumTipoSelecionado selecionaTipoQuestao={selecionaTipoQuestao} />
      </Show>
      <Botoes {...props} questaoId={questaoId} />
    </EditorLayout>
  )
}

const Botoes = (props: IProps) => {
  const { buttons, onQuestaoReview, onQuestaoSave, onCancel, onQuestaoDelete, questaoId } = props
  const safeButtons = buttons || []
  const { Layout, SecondaryActions, PrimaryActions } = Botoes
  const showCancelar = safeButtons.includes(TIPO_BOTAO.CANCELAR)
  const showSalvar = safeButtons.includes(TIPO_BOTAO.SALVAR)
  const showRevisar = safeButtons.includes(TIPO_BOTAO.REVISAR)
  const showExcluir = safeButtons.includes(TIPO_BOTAO.EXCLUIR)
  const dispatch = questaoEditorStore.useDispatch()
  const allState = questaoEditorStore.useState()
  const state = allState[questaoId] || { questao: {} }
  const questaoPersistidaId =
    typeof state?.questao?.id === 'number' && Number.isFinite(state.questao.id)
      ? state.questao.id
      : null
  const canShowExcluir = showExcluir && !!questaoPersistidaId

  function handleClickSalvar() {
    dispatch(
      questaoEditorStore.thunks.createOrUpdateQuestao(
        { ...state.questao, revisada: false },
        questaoId,
        onQuestaoSave
      )
    )
  }

  function handleRevisar() {
    const questao = { ...state.questao, revisada: true }
    dispatch(questaoEditorStore.thunks.createOrUpdateQuestao(questao, questaoId, onQuestaoReview))
  }

  function handleCancelar() {
    dispatch(questaoEditorStore.actions.resetQuestao({ questaoId }))
    onCancel?.()
  }

  function handleDelete() {
    if (!questaoPersistidaId) return
    Modal.confirm({
      title: 'Excluir questão',
      content: 'Essa ação não pode ser desfeita. Deseja continuar?',
      okText: 'Excluir',
      cancelText: 'Cancelar',
      okType: 'danger',
      onOk: () => {
        dispatch(
          questaoEditorStore.thunks.deleteQuestao(String(questaoPersistidaId), () => {
            onQuestaoDelete?.(questaoPersistidaId)
            onCancel?.()
          })
        )
      },
    })
  }

  return (
    <Layout>
      <SecondaryActions>
        <Show condition={canShowExcluir}>
          <Button negative type="button" onClick={handleDelete} disabled={state.isLoading}>
            Excluir
          </Button>
        </Show>
        <Show condition={showCancelar}>
          <Button type="button" onClick={handleCancelar} disabled={state.isLoading}>
            Cancelar
          </Button>
        </Show>
      </SecondaryActions>

      <PrimaryActions>
        <Show condition={showRevisar}>
          <Button
            positive={state.hasChange}
            secondary={!state.hasChange}
            type="button"
            onClick={handleRevisar}
            loading={state.isLoading}
            disabled={state.isLoading || state?.questao?.revisada}
          >
            {state.questao.revisada ? 'Revisada' : 'Revisar'}
          </Button>
        </Show>
        <Show condition={showSalvar}>
          <Button primary type="button" onClick={handleClickSalvar} loading={state.isLoading}>
            Salvar
          </Button>
        </Show>
      </PrimaryActions>
    </Layout>
  )
}
Botoes.Layout = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid #ececec;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    align-items: stretch;
  }
`

Botoes.SecondaryActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`

Botoes.PrimaryActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`

interface NenhumTipoSelecionadoType {
  selecionaTipoQuestao: (tipoQuestao: TIPO_QUESTAO) => any
}
const NenhumTipoSelecionado = ({ selecionaTipoQuestao }: NenhumTipoSelecionadoType) => {
  return (
    <TipoQuestaoContainer>
      <TipoQuestaoTitle>Qual o tipo de questão deseja criar?</TipoQuestaoTitle>
      <TipoQuestaoGrid>
        <TipoQuestaoButton onClick={() => selecionaTipoQuestao(TIPO_QUESTAO.MULTIPLA_ESCOLHA)}>
          Múltipla Escolha
        </TipoQuestaoButton>
        <TipoQuestaoButton onClick={() => selecionaTipoQuestao(TIPO_QUESTAO.RELACIONAR_ITENS)}>
          Relacionar Item (texto ou imagem) A outro item (outro texto ou outra imagem)
        </TipoQuestaoButton>
        <TipoQuestaoButton
          onClick={() => selecionaTipoQuestao(TIPO_QUESTAO.RELACIONAR_ITEM_A_IMAGEM)}
        >
          Relacionar item (texto ou imagem) à ponto na imagem
        </TipoQuestaoButton>
        <TipoQuestaoButton
          onClick={() => selecionaTipoQuestao(TIPO_QUESTAO.MULTIPLO_VERDADEIRO_FALSO)}
        >
          Múltiplo Verdadeiro ou Falso
        </TipoQuestaoButton>
      </TipoQuestaoGrid>
    </TipoQuestaoContainer>
  )
}

const Show = ({ condition, children }) => (!condition ? null : children)

const EditorLayout = styled.div`
  color: #2f2f2f;
`

const SectionHeader = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #343434;
  margin-bottom: 8px;
`

const EditorBlock = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
`

const SectionContent = styled.div`
  margin-top: 14px;
`

const TipoQuestaoContainer = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  padding: 16px;
  background: #fafafa;
`

const TipoQuestaoTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 600;
`

const TipoQuestaoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const TipoQuestaoButton = styled.button`
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: #fff;
  text-align: left;
  padding: 12px;
  cursor: pointer;
  color: #2f2f2f;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #1677ff;
    box-shadow: 0 0 0 3px rgba(22, 119, 255, 0.12);
  }
`
